/**
 * Query-key factories — the dashboard's public contract with
 * react-query.
 *
 * Every hook that uses `useQuery` reads its key from one of these
 * factories; every mutation that invalidates reads the same factory
 * to build its invalidation target. Centralising keeps Stage 5 from
 * accidentally typo'ing a key and causing a phantom cache miss.
 *
 * Convention (matches TanStack's own `QueryKeyFactory` shape):
 *   - Level 0: resource name ("workspace", "audits", …).
 *   - Level 1: scope ("list", "detail", "bySite", …).
 *   - Level 2+: inputs (filter object, uuid, …).
 *
 * The `all` entry at each level is used for broad invalidation:
 *   `qc.invalidateQueries({ queryKey: auditKeys.all })` refreshes
 *   every audit-related query (list + every detail).
 *
 * Keys MUST be JSON-serialisable (no classes, no Dates).
 *
 * File name is `query-keys.ts` (not `keys.ts`) to avoid colliding
 * with the `keys.ts` hook file for API-keys. Don't rename.
 */

export const workspaceKeys = {
  all: ['workspace'] as const,
  /**
   * Scoped by Clerk org id so a cross-org navigation (org switch,
   * token rotation, or a stale tab resuming with a different claim)
   * invalidates cleanly. Pre-fix this was `['workspace', 'current']`
   * — unkeyed — and a manual `queryClient.clear()` in the sidebar
   * was the only thing stopping a stale cache from leaking between
   * orgs. Any path that changed the active org WITHOUT going
   * through that handler (Clerk's own `<OrganizationSwitcher>`,
   * deep link, checkout-drawer `setActive`) served the previous
   * org's data for up to `staleTime`, which is the exact class of
   * bug behind the plan-display inconsistency. Keying here is
   * defence-in-depth and costs nothing.
   */
  current: (orgId: string | null | undefined) => ['workspace', 'current', orgId ?? null] as const,
} as const;

export const usageKeys = {
  all: ['usage'] as const,
  // Scoped by Clerk org id so an org-switch invalidates cleanly.
  byOrg: (orgId: string | null | undefined) => ['usage', orgId ?? null] as const,
} as const;

export const auditKeys = {
  all: ['audits'] as const,
  lists: () => ['audits', 'list'] as const,
  list: (filter: { limit?: number; offset?: number } = {}) => ['audits', 'list', filter] as const,
  details: () => ['audits', 'detail'] as const,
  detail: (id: string) => ['audits', 'detail', id] as const,
  report: (id: string) => ['audits', 'report', id] as const,
} as const;

export const siteKeys = {
  all: ['sites'] as const,
  lists: () => ['sites', 'list'] as const,
  list: (filter: { limit?: number; offset?: number } = {}) => ['sites', 'list', filter] as const,
  details: () => ['sites', 'detail'] as const,
  detail: (uuid: string) => ['sites', 'detail', uuid] as const,
  overview: (uuid: string, days?: number) => ['sites', 'overview', uuid, days ?? null] as const,
  latest: (uuid: string, filter: { page_uuid?: string; device_profile_label?: string } = {}) =>
    ['sites', 'latest', uuid, filter] as const,
  p75: (
    uuid: string,
    filter: {
      days?: number;
      device_type?: string;
      device_profile_label?: string;
      region?: string;
    } = {},
  ) => ['sites', 'p75', uuid, filter] as const,
  trend: (
    uuid: string,
    filter: {
      interval?: 'hour' | 'day';
      range?: number;
      page_uuid?: string;
      device_type?: string;
      device_profile_label?: string;
      region?: string;
    } = {},
  ) => ['sites', 'trend', uuid, filter] as const,
  settings: (uuid: string) => ['sites', 'settings', uuid] as const,
  pages: (uuid: string) => ['sites', 'pages', uuid] as const,
} as const;

export const pageKeys = {
  all: ['pages'] as const,
  detail: (uuid: string) => ['pages', 'detail', uuid] as const,
  p75: (
    uuid: string,
    filter: {
      days?: number;
      device_type?: string;
      device_profile_label?: string;
      region?: string;
    } = {},
  ) => ['pages', 'p75', uuid, filter] as const,
  samples: (
    uuid: string,
    filter: {
      days?: number;
      region?: string;
      device_type?: string;
      device_profile_label?: string;
    } = {},
  ) => ['pages', 'samples', uuid, filter] as const,
} as const;

export const monitorKeys = {
  all: ['monitors'] as const,
  list: () => ['monitors', 'list'] as const,
  detail: (siteUuid: string) => ['monitors', 'detail', siteUuid] as const,
} as const;

export const keyKeys = {
  all: ['api-keys'] as const,
  list: () => ['api-keys', 'list'] as const,
} as const;

export const memberKeys = {
  all: ['members'] as const,
  list: (orgUuid: string) => ['members', 'list', orgUuid] as const,
} as const;

export const billingKeys = {
  all: ['billing'] as const,
  current: () => ['billing', 'current'] as const,
  plans: () => ['billing', 'plans'] as const,
} as const;

export const webhookKeys = {
  all: ['webhooks'] as const,
  list: () => ['webhooks', 'list'] as const,
  detail: (uuid: string) => ['webhooks', 'detail', uuid] as const,
  messages: (
    filter: {
      endpoint_uuid?: string;
      event_type?: string;
      status?: string;
      limit?: number;
    } = {},
  ) => ['webhooks', 'messages', filter] as const,
  attempts: (messageUuid: string) => ['webhooks', 'attempts', messageUuid] as const,
} as const;

export const integrationKeys = {
  all: ['integrations'] as const,
  list: () => ['integrations', 'list'] as const,
} as const;

export const searchKeys = {
  all: ['search'] as const,
  query: (q: string) => ['search', q] as const,
} as const;
